/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@mybridge/ui';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { DiskContext } from '../context';
import { Controller, useForm } from 'react-hook-form';
import { FileFolderIcon } from '../my-disk/file-icon';
import { useSelector } from 'react-redux';
import {
  copyTextToClipboard,
  getFullFileName,
  getUserFullName,
  getUserProfilePic,
} from 'v4/lib/commons';
import { LinkIcon } from '@mybridge/icons';
import { ConnectionsDropdown } from 'v4/components/connections-dropdown';
import { UserCardSkeleton } from 'v4/components/user-card-skeleton';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { useBranchLink } from 'v4/components/branch-sdk/branch-create';
export const DiskShareFileModal = forwardRef(({ ...props }, ref) => {
  const { allDegreeConnectionsArr } =
    useSelector((state) => state.connection) ?? {};
  const { userProfileInfo } = useSelector((state) => state.userProfile) ?? {};
  const {
    shareDisc,
    createNewFolder,
    fileSharedWithUsers,
    refetchFileSharedWithUsers,
    refetchSharedFileLink,
    createFolderLoading,
    selectedFile,
    sharedFileLink,
    generateShareLink,
    generateShareLinkLoading,
    fileSharedWithUsersLoading,
    fileSharedWithUsersFetching,
    sharedFileLinkLoading,
    deleteShareLink,
    shareFileWithUsers,
    shareFileWithUsersLoading,
    unshareFileWithUsers,
    unshareFileWithUsersLoading,
    deleteShareLinkLoading,
  } = useContext(DiskContext);
  const allDegreeConnections = allDegreeConnectionsArr?.[0] ?? [];
  const [selected, setSelected] = useState([]);
  const [linkAccessLevel, setLinkAccessLevel] = useState(1);
  const [shareAccessLevel, setShareAccessLevel] = useState(1);
  const { t } = useTranslator();
  const direction = useDirection(); 
  const { isReady, createBranchLink } = useBranchLink();
  
  const toast = useToast({
    position: 'top',
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = ({ name }) => {
    createNewFolder(name);
  };

  useEffect(() => {
    if (shareDisc.isOpen && selectedFile?.id) {
      refetchFileSharedWithUsers?.();
      refetchSharedFileLink?.();
    }
  }, [shareDisc.isOpen, selectedFile?.id]);

  const getInviteByUser = (id) => {
    return userProfileInfo?.id === id
      ? userProfileInfo
      : allDegreeConnections?.find((c) => c?.id === id);
  };

  const generateLink = async() => {
    const url = await createBranchLink({
      itemType: 'disk',
      itemId: selectedFile?.id,
      itemData: {
        "accessType": 1,
        "name": selectedFile?.type==='F' ? selectedFile?.name : 'Directory',
        "type": selectedFile?.type,
        "file": selectedFile?.file || null
      },
    });
    generateShareLink({
      accessType: linkAccessLevel,
      id: selectedFile?.id,
      public_share_deep_link:url
    });
  };

  const deleteLink = () => {
    deleteShareLink({
      id: selectedFile?.id,
    });
  };

  const onCopyLinkClick = () => {
    try {
      copyTextToClipboard(sharedFileLink?.public_share_deep_link);
      toast({ title: t('myDisk.linkCopiedSuccessfully') || 'Link copied successfully!', status: 'success' ,position: 'top'});
    } catch (e) {
      toast({ title: e.message, status: 'error' });
    }
  };

  const onShare = async (form) => {
    if (form?.usersToShareWith?.length) {
      await shareFileWithUsers({
        id: selectedFile?.id,
        shared_with_id: form?.usersToShareWith,
        access_type: shareAccessLevel,
      });
      setValue?.('usersToShareWith', []);
      handleOnClose()
    }
  };

  const onChangeShareAccess = async ({ userId, accessLevel }) => {
    if (accessLevel == 0) {
      await unshareFileWithUsers({
        id: selectedFile?.id,
        shared_with_id: userId,
      });
    } else {
      await shareFileWithUsers({
        id: selectedFile?.id,
        shared_with_id: [userId],
        access_type: accessLevel,
      });
    }
    setValue?.('usersToShareWith', []);
  };

  const handleOnClose = () => {
    shareDisc?.onClose?.();
  }
  return (
    <>
      <Modal size="xl" {...shareDisc} onClose={handleOnClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onShare)}>
            <ModalCloseButton {...(direction === 'rtl') ? {left: '0.75rem', right: 'auto'} : {left: 'auto', right: '0.75rem'}} />
            <ModalHeader>
              <HStack spacing={3} alignItems={'flex-start'}>
                <FileFolderIcon file={selectedFile} width={46} height={46} />
                <Stack spacing={0} gap={0}>
                  <Heading size="md">{t('post.share') || "Share"}</Heading>
                  <Text truncate maxW="80" fontSize="sm" fontWeight="normal">
                    {getFullFileName(selectedFile)}
                    {/* {selectedFile?.name}
                    {selectedFile?.extension
                      ? '.' + selectedFile?.extension
                      : ''} */}
                  </Text>
                </Stack>
              </HStack>
            </ModalHeader>
            <ModalBody>
              {/* Users list */}
              {fileSharedWithUsersLoading ? (
                <Stack>
                  <UserCardSkeleton />
                </Stack>
              ) : (
                <>
                  {fileSharedWithUsers?.length ? (
                    <Stack pb={4}>
                      <Heading mb={2} size="sm">
                        {' '}
                        {t('otherModules.peopleWithAccess') || "People with access"}
                      </Heading>
                      <List as={Stack} maxH="300px" overflowY="auto">
                        {fileSharedWithUsers?.map((conn, connIndex) => (
                          <ListItem
                            borderRadius={5}
                            _hover={{ bg: 'brandGray.500' }}
                            p={2}
                            as={HStack}
                            key={connIndex}
                          >
                            <Avatar
                              src={getUserProfilePic(conn?.shared_with)}
                              name={getUserFullName(conn?.shared_with)}
                            />
                            <Stack flex={1} spacing={0}>
                              <Heading size="sm">
                                {getUserFullName(conn?.shared_with)}
                              </Heading>
                              <Text fontSize="sm">
                                {t('otherModules.invitedBy') || "Invited by"}{' '}
                                {getUserFullName(
                                  getInviteByUser(conn?.shared_by)
                                )}
                              </Text>
                            </Stack>
                            {userProfileInfo?.id === conn?.shared_by ||
                            userProfileInfo?.id === selectedFile?.created_by ? (
                              <Select
                                p={0}
                                bg="transparent"
                                border="none"
                                // maxW="25%"
                                w="max-content"
                                defaultValue={conn.access ?? 1}
                                onChange={(e) =>
                                  onChangeShareAccess({
                                    userId: conn?.shared_with?.id,
                                    accessLevel: e?.target?.value,
                                  })
                                }
                              >
                                <option value={2}>{t('otherModules.canEdit') || "Can Edit"}</option>
                                <option value={1}>{t('otherModules.canView') || "Can View"}</option>
                                <option value={0}>{t('otherModules.removeAccess') || "Remove Access"}</option>
                              </Select>
                            ) : (
                              ''
                            )}
                          </ListItem>
                        ))}
                      </List>
                      <Divider />
                    </Stack>
                  ) : (
                    ''
                  )}
                </>
              )}
              {/* Actions */}
              {sharedFileLinkLoading ? (
                <Skeleton w="100%">
                  {/* <SkeletonText noOfLines={2} /> */}
                </Skeleton>
              ) : selectedFile?.created_by !== userProfileInfo?.id ? (
                <></>
              ) : (
                <Stack spacing={2}>
                  <Heading size="sm">{t('myCalendar.copyLink') || "Copy link"}</Heading>
                  {sharedFileLink ? (
                    <>
                      <HStack w="100%">
                        <HStack
                          w="50%"
                          href={sharedFileLink?.public_share_deep_link}
                          _target="blank"
                        >
                          <LinkIcon width={20} height={20} />
                          <Text as={Link} maxW="70%" truncate>
                            {sharedFileLink?.public_share_deep_link}
                          </Text>
                        </HStack>
                        <HStack justifyContent="flex-end" w="50%">
                          <Link
                            onClick={onCopyLinkClick}
                            textDecoration="underline"
                          >
                            {t('myCalendar.copyLink') || "Copy link"}
                          </Link>
                          {deleteShareLinkLoading ? (
                            <Spinner />
                          ) : (
                            <Link
                              color="red"
                              onClick={deleteLink}
                              textDecoration="underline"
                            >
                              {t('otherModules.deleteLink') || "Delete Link"}
                            </Link>
                          )}
                        </HStack>
                      </HStack>
                    </>
                  ) : (
                    <>
                      <HStack>
                        <Text flex={1}>{t('otherModules.noLinkCreated') || "No link created yet"}</Text>
                        <HStack>
                          <Select
                            value={linkAccessLevel}
                            onChange={(e) =>
                              setLinkAccessLevel(e?.target?.value)
                            }
                            bg="transparent"
                            border="none"
                            borderRadius="full"
                            sx={direction === 'rtl' ? {
                              '&': {
                                textAlign: 'left',
                              }
                            } : {}}
                          >
                            <option value={2}>{t('otherModules.canEdit') || "Can Edit"}</option>
                            <option value={1}>{t('otherModules.canView') || "Can View"}</option>
                          </Select>
                          {generateShareLinkLoading ? (
                            <Spinner />
                          ) : (
                            <Link
                              flex={1}
                              whiteSpace="nowrap"
                              textDecoration="underline"
                              onClick={generateLink}
                            >
                              {t('otherModules.createLink') || "Create a link"}
                            </Link>
                          )}
                        </HStack>
                      </HStack>
                    </>
                  )}
                </Stack>
              )}

              {/* Connections list */}
              {/* {selectedFile?.} */}
              <Stack pt={4}>
                <Heading size="sm">{t('otherModules.shareWith') || "Share with"}</Heading>
                <HStack alignItems="flex-end">
                  <Box w='full'>
                    <Controller
                    render={({ field }) => <ConnectionsDropdown {...field} />}
                    control={control}
                    name="usersToShareWith"
                    rules={{ required: true }}
                  />
                  </Box>
                  <Box>
                  <Select
                    borderRadius="full"
                    value={shareAccessLevel}
                    onChange={(e) => setShareAccessLevel(e?.target?.value)}
                    maxW={["full"]}
                    minW={['120px', '130px']}
                    sx={direction === 'rtl' ? {
                      '&': {
                        textAlign: 'left',
                      },
                    } : {}}
                    fontSize={["14px", "16px"]}
                  >
                    <option value={2}>{t('otherModules.canEdit') || "Can Edit"}</option>
                    <option value={1}>{t('otherModules.canView') || "Can View"}</option>
                  </Select>
                  </Box>
                </HStack>
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Button
                isDisabled={watch('usersToShareWith')?.length <= 0}
                isLoading={shareFileWithUsersLoading}
                type="submit"
                onClick={onShare}
                variant="primary"
                borderRadius="30px"
              >
                {t('post.share') || "Share"}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
});
